/* nexusAuth.ts */
import axios from 'axios';
// Use the unified secure storage wrapper so that auth can be kept either in
// cookies (when VUE_APP_USE_COOKIE_AUTH=true) or localStorage (legacy mode)
import { setAccessToken, clearAuth } from '@/services/secureStorage';

// Name of the localStorage item for Nexus authentication
const NEXUS_AUTH_TOKEN =
  process.env.VUE_APP_USE_COOKIE_AUTH === 'true' ? 'access_token' : 'apollo-token';

/**
 * Sets up authentication for the Nexus Apollo client
 * This replaces the old onLogin function specifically for Nexus authentication
 *
 * @param token - The access token from Nexus authentication
 */
export async function onNexusLogin(token: string): Promise<void> {
  if (token) {
    // Persist token via secureStorage – will choose cookies or localStorage based
    // on the VUE_APP_USE_COOKIE_AUTH feature flag.
    setAccessToken(token);

    // Ensure backward-compatibility: also set in localStorage when cookie mode
    // is on so that existing code that still checks localStorage continues to
    // work until we fully migrate.
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('apollo-token', token);
    }

    // Set axios default Authorization header for REST API calls
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;

    try {
      // Fetch user permissions using the Nexus token
      // Note: This might need to be updated if Nexus has a different permissions endpoint
      const response = await axios.get(`${process.env.VUE_APP_AUTH0}/permissions`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response?.data?.data) {
        localStorage.setItem('permissions', response?.data?.data?.permissions);
        localStorage.setItem('statistics', JSON.stringify(response?.data?.data?.statistics));
      }
    } catch (permissionsError) {
      console.warn(
        'Could not fetch permissions with Nexus token, this may be expected if using Nexus-only auth:',
        permissionsError
      );
      // Don't fail the login process if permissions fetch fails
      // This allows for Nexus-only authentication without Auth0 dependencies
    }
  }

  // The Nexus Apollo client will automatically pick up the token from localStorage
  // due to the auth link we set up in nexusClient.ts
  console.log('Nexus authentication setup completed');
}

/**
 * Handles logout for the Nexus Apollo client
 * This replaces the old onLogout function specifically for Nexus
 */
export async function onNexusLogout(): Promise<void> {
  // Clear tokens and related auth artefacts
  clearAuth();

  if (typeof localStorage !== 'undefined') {
    // Some areas of the legacy code still rely on these keys – remove them
    localStorage.removeItem('apollo-token');
    localStorage.removeItem('apollo-refresh-token');
    localStorage.removeItem('user');
  }

  // Clear axios default Authorization header
  delete axios.defaults.headers.common.Authorization;

  console.log('Nexus logout completed');
}

/**
 * Checks if the user is authenticated with Nexus
 */
export function isNexusAuthenticated(): boolean {
  return !!localStorage.getItem('apollo-token');
}

/**
 * Gets the current Nexus authentication token
 */
export function getNexusToken(): string | null {
  return localStorage.getItem('apollo-token');
}
